import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const EventDetailsModal = ({ event, isRegistered, onClose, onRegister, onCancel }) => {
  if (!event) return null;

  const {
    id,
    title,
    description,
    event_type,
    location,
    start_time,
    end_time,
    max_attendees,
    registration_deadline,
    requirements,
    image_url,
    organizer
  } = event;

  const startDate = new Date(start_time);
  const endDate = new Date(end_time);
  const deadline = registration_deadline ? new Date(registration_deadline) : null;
  const isPast = startDate < new Date();
  const isDeadlinePassed = deadline && deadline < new Date();

  const eventTypeColors = {
    workshop: 'bg-blue-500',
    seminar: 'bg-purple-500',
    competition: 'bg-red-500',
    social: 'bg-green-500',
    special: 'bg-yellow-500'
  };

  return (
    <div 
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto overflow-elegant"
        onClick={(e) => e?.stopPropagation()}
      >
        {image_url && (
          <div className="relative h-64">
            <Image
              src={image_url}
              alt={`${title} event image`}
              className="w-full h-full object-cover"
            />
            <div className={`absolute top-4 right-4 ${eventTypeColors?.[event_type]} text-white px-4 py-2 rounded-full text-sm font-semibold`}>
              {event_type?.charAt(0)?.toUpperCase() + event_type?.slice(1)}
            </div>
          </div>
        )}

        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between gap-4 mb-6">
            <div className="flex-1">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                {title}
              </h2>
              {organizer && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="User" size={16} />
                  <span>Organized by {organizer?.full_name}</span>
                </div>
              )}
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              iconName="X"
              iconSize={20}
              aria-label="Close modal"
            />
          </div>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="FileText" size={20} color="var(--color-primary)" />
                Description
              </h3>
              <p className="text-foreground leading-relaxed">
                {description}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Info" size={20} color="var(--color-primary)" />
                Event Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Icon name="Calendar" size={20} className="text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground">
                      {startDate?.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {startDate?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - 
                      {endDate?.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </p>
                  </div>
                </div>

                {location && (
                  <div className="flex items-center gap-3">
                    <Icon name="MapPin" size={20} className="text-primary" />
                    <span className="text-foreground">{location}</span>
                  </div>
                )}

                {max_attendees && (
                  <div className="flex items-center gap-3">
                    <Icon name="Users" size={20} className="text-primary" />
                    <span className="text-foreground">Maximum {max_attendees} attendees</span>
                  </div>
                )}

                {deadline && (
                  <div className="flex items-center gap-3">
                    <Icon name="Clock" size={20} className="text-primary" />
                    <span className="text-foreground">
                      Registration deadline: {deadline?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {requirements && (
              <div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="CheckCircle2" size={20} color="var(--color-primary)" />
                  Requirements
                </h3>
                <p className="text-foreground">{requirements}</p>
              </div>
            )}

            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
              {isRegistered ? (
                <div className="flex items-center gap-2 text-success">
                  <Icon name="CheckCircle2" size={20} />
                  <span className="font-medium">You are registered</span>
                </div>
              ) : isPast ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Icon name="Clock" size={20} />
                  <span className="font-medium">This event has passed</span>
                </div>
              ) : isDeadlinePassed ? (
                <div className="flex items-center gap-2 text-warning">
                  <Icon name="AlertCircle" size={20} />
                  <span className="font-medium">Registration closed</span>
                </div>
              ) : (
                <span className="text-muted-foreground">Ready to join?</span>
              )}

              <div className="flex gap-2">
                {!isPast && !isDeadlinePassed && (
                  isRegistered ? (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        onCancel(id);
                        onClose();
                      }}
                      iconName="X"
                      iconPosition="left"
                    >
                      Cancel Registration
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={() => {
                        onRegister(id);
                        onClose();
                      }}
                      iconName="Check"
                      iconPosition="left"
                    >
                      Register Now
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
